import React from 'react';
import { Container, Paper, Box, Typography, Button, Alert, CircularProgress, IconButton, Tooltip, useTheme, useMediaQuery, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  ArrowBack,
  Download,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  FullscreenExit,
  Description,
  VerifiedUser,
  AttachFile,
} from '@mui/icons-material';
import { Document, Page } from 'react-pdf';
import useViewInvoice from '../../../hooks/purchaseInvoices/pages/useViewInvoice';
import {
  paperMain,
  paperInfo,
  paperFile,
  pdfToolbar,
  pdfContainer,
  pdfPager
} from '../../../styles/purchaseInvoices/pages/viewInvoiceStyle';

const ViewInvoice = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  const {
    invoice,
    invoiceFile,
    loading,
    error,
    numPages,
    pageNumber,
    setPageNumber,
    scale,
    setScale,
    isFullscreen,
    pdfError,
    handleLogin,
    handleBackToSystem,
    handleViewAttachments,
    handleDownload,
    handleZoomIn,
    handleZoomOut,
    toggleFullscreen,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    getPdfSrc,
    invoiceVersion,
    toggleInvoiceVersion,
    hasAttachments
  } = useViewInvoice();

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            טוען חשבונית...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={handleLogin}>
            התחבר למערכת
          </Button>
        </Box>
      </Container>
    );
  }

  if (!invoice && !invoiceFile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            חשבונית לא נמצאה
          </Alert>
          <Button variant="contained" onClick={handleBackToSystem}>
            חזרה למערכת
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={isFullscreen ? "xl" : "lg"} sx={{ py: isMobile ? 2 : 4 }}>
      <Paper elevation={0} sx={paperMain(isMobile)}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 2 : 0 }}>
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="500" color="text.primary" sx={{ mb: 1 }}>
              חשבונית מספר {invoice?.booknum}
            </Typography>
            <Typography variant={isMobile ? "body2" : "body2"} color="text.secondary">
              צפייה ועריכת חשבונית
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'row' : 'row', gap: 2, width: isMobile ? '100%' : 'auto' }}>
            {console.log('hasAttachments:', hasAttachments, 'invoice id:', invoice?.id)}
            {hasAttachments && (
              <Button 
                variant="outlined" 
                onClick={handleViewAttachments} 
                sx={{ 
                  borderRadius: 2, 
                  borderColor: '#e0e0e0', 
                  color: 'text.primary', 
                  flex: isMobile ? 1 : 'none', 
                  minHeight: isMobile ? 44 : 'auto', 
                  '&:hover': { 
                    borderColor: '#d0d0d0', 
                    backgroundColor: '#f8f9fa' 
                  } 
                }} 
                startIcon={<AttachFile />}
              >
                {isMobile ? 'צרופות' : 'צפייה בצרופות'}
              </Button>
            )}
            <Button variant="outlined" onClick={handleBackToSystem} sx={{ borderRadius: 2, borderColor: '#e0e0e0', color: 'text.primary', flex: isMobile ? 1 : 'none', minHeight: isMobile ? 44 : 'auto', '&:hover': { borderColor: '#d0d0d0', backgroundColor: '#f8f9fa' } }} startIcon={<ArrowBack />}>
              {isMobile ? 'חזרה' : 'חזרה למערכת'}
            </Button>
            <Button variant="contained" onClick={handleDownload} disabled={!invoiceFile} sx={{ borderRadius: 2, backgroundColor: '#1976d2', flex: isMobile ? 1 : 'none', minHeight: isMobile ? 44 : 'auto', '&:hover': { backgroundColor: '#1565c0' } }} startIcon={<Download />}>
              הורדה
            </Button>
          </Box>
        </Box>
      </Paper>

      {invoice && (
        <Paper elevation={0} sx={paperInfo(isMobile)}>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: isMobile ? 2 : 4, alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: isMobile ? '100%' : 'auto' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                ספק
              </Typography>
              <Typography variant="h6" fontWeight="600" color="text.primary">
                {invoice.supName}
              </Typography>
            </Box>
            <Box sx={{ minWidth: isMobile ? '100%' : 'auto' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                תאריך
              </Typography>
              <Typography variant="h6" fontWeight="600" color="text.primary">
                {invoice.ivDate}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {invoiceFile && (
        <Paper elevation={0} sx={paperFile}>          <Box sx={pdfToolbar}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="הקטן">
                <IconButton onClick={handleZoomOut} disabled={scale <= 0.5}>
                  <ZoomOut />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </Typography>
              <Tooltip title="הגדל">
                <IconButton onClick={handleZoomIn} disabled={scale >= 3.0}>
                  <ZoomIn />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>              {/* Version Toggle Button - only show if both versions are available */}
              {invoice && invoice.hasOriginalVersion && (
                <ToggleButtonGroup
                  value={invoiceVersion}
                  exclusive
                  onChange={toggleInvoiceVersion}
                  size="small"
                  sx={{
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: 1.5,
                    '.MuiToggleButton-root': {
                      border: 'none',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      lineHeight: 1,
                    },
                  }}
                >
                  <ToggleButton 
                    value="original" 
                    sx={{ 
                      borderRadius: '4px 0 0 4px',
                      bgcolor: invoiceVersion === 'original' ? 'primary.light' : 'transparent',
                      color: invoiceVersion === 'original' ? 'primary.contrastText' : 'text.secondary',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                      },
                      '&:hover': {
                        bgcolor: invoiceVersion === 'original' ? 'primary.main' : 'action.hover',
                      }
                    }}
                  >
                    <Description fontSize="small" sx={{ fontSize: '1rem', mr: 0.5 }} />
                    מקורית
                  </ToggleButton>
                  <ToggleButton 
                    value="signed" 
                    sx={{ 
                      borderRadius: '0 4px 4px 0',
                      bgcolor: invoiceVersion === 'signed' ? 'primary.light' : 'transparent',
                      color: invoiceVersion === 'signed' ? 'primary.contrastText' : 'text.secondary',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                      },
                      '&:hover': {
                        bgcolor: invoiceVersion === 'signed' ? 'primary.main' : 'action.hover',
                      }
                    }}                  >
                  <VerifiedUser fontSize="small" sx={{ fontSize: '1rem', mr: 0.5 }} />
                    חתומה
                  </ToggleButton>
                </ToggleButtonGroup>
              )}

              {numPages && (
                <Typography variant="body2" sx={{ mr: 1 }}>
                  עמוד {pageNumber} מתוך {numPages}
                </Typography>
              )}
              <Tooltip title={isFullscreen ? "יציאה ממסך מלא" : "מסך מלא"}>
                <IconButton onClick={() => toggleFullscreen(isMobile)}>
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box className="pdf-container" sx={pdfContainer(isFullscreen)}>
            {(() => {
              const pdfSrc = getPdfSrc();
              if (pdfSrc && !pdfError) {
                return (
                  <Document
                    file={pdfSrc}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    error={
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          שגיאה בטעינת קובץ PDF
                        </Typography>
                        <Button variant="contained" onClick={handleDownload} sx={{ mt: 2 }}>
                          הורד קובץ PDF
                        </Button>
                      </Box>
                    }
                    loading={
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          טוען PDF...
                        </Typography>
                      </Box>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      loading={<CircularProgress size={30} />}
                    />
                  </Document>
                );
              } else if (invoiceFile.startsWith('data:image/') || invoiceFile.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <img
                      src={invoiceFile}
                      alt="Invoice"
                      style={{ maxWidth: '100%', height: 'auto', transform: `scale(${scale})`, transformOrigin: 'center top' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <Box sx={{ display: 'none', textAlign: 'center' }}>
                      <Typography variant="body2" color="error">
                        שגיאה בטעינת התמונה
                      </Typography>
                      <Button variant="outlined" onClick={handleDownload} sx={{ mt: 2 }}>
                        הורד קובץ
                      </Button>
                    </Box>
                  </Box>
                );
              } else if (invoiceFile.startsWith('http')) {
                return (
                  <iframe
                    src={invoiceFile}
                    width="100%"
                    height="100%"
                    style={{ border: 'none', minHeight: '600px' }}
                  />
                );
              } else {
                return (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      סוג קובץ לא נתמך לתצוגה
                    </Typography>
                    <Button variant="contained" onClick={handleDownload} sx={{ mt: 2 }}>
                      הורד קובץ
                    </Button>
                  </Box>
                );
              }
            })()}
          </Box>
          {numPages > 1 && (
            <Box sx={pdfPager}>
              <Button variant="outlined" size="small" disabled={pageNumber <= 1} onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))} sx={{ borderRadius: 2 }}>
                עמוד קודם
              </Button>
              <Button variant="outlined" size="small" disabled={pageNumber >= numPages} onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))} sx={{ borderRadius: 2 }}>
                עמוד הבא
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default ViewInvoice;